import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import AgendaRow from "./AgendaRow";
import { romeToday, shiftDay } from "./agendaConfig";

export const weekStart = (iso) => {
  const dow = (new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7;
  return shiftDay(iso, -dow);
};

const fmt = (iso, opts) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("it-IT", { timeZone: "UTC", ...opts });

export const dayStats = (rows) => {
  const active = rows.filter((r) => r.status !== "annullata");
  return {
    count: active.length,
    pax: active.filter((r) => r.status !== "no_show").reduce((s, r) => s + (Number(r.pax) || 0), 0),
  };
};

function WeekNav({ start, onChange }) {
  const end = shiftDay(start, 6);
  const today = romeToday();
  const isCurrent = today >= start && today <= end;
  const label = `${fmt(start, { day: "numeric", month: "short" })} – ${fmt(end, { day: "numeric", month: "short", year: "numeric" })}`;
  const shortLabel = `${fmt(start, { day: "numeric", month: "numeric" })} – ${fmt(end, { day: "numeric", month: "numeric" })}`;
  const btn = "w-11 h-11 rounded-full border border-sand-300 bg-white flex items-center justify-center hover:border-bronze-400 active:scale-95 transition-[transform,border-color]";
  return (
    <div className="flex items-center gap-2" data-testid="week-nav">
      <button type="button" className={btn} onClick={() => onChange(shiftDay(start, -7))} aria-label="Settimana precedente" data-testid="week-prev">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <label className="relative flex-1 min-w-0 h-11 rounded-full border border-sand-300 bg-white px-3 sm:px-4 flex items-center gap-2 cursor-pointer hover:border-bronze-400 transition-colors">
        <CalendarDays className="w-4 h-4 text-bronze-600 shrink-0" />
        <span className="font-data truncate text-sm font-medium text-ink-900" data-testid="week-label">
          <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{shortLabel}</span>
        </span>
        {isCurrent && <span className="hidden sm:inline shrink-0 text-[11px] rounded-full bg-bronze-100 text-bronze-800 px-2 py-0.5" data-testid="week-current-badge">Settimana corrente</span>}
        <input type="date" value={start} aria-label="Vai alla data" data-testid="week-date-input"
               onChange={(e) => e.target.value && onChange(e.target.value)}
               className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
      </label>
      <button type="button" className={btn} onClick={() => onChange(shiftDay(start, 7))} aria-label="Settimana successiva" data-testid="week-next">
        <ChevronRight className="w-5 h-5" />
      </button>
      <button type="button" onClick={() => onChange(today)} disabled={isCurrent} data-testid="week-today-btn"
              className={`h-11 px-4 rounded-full text-sm font-medium transition-colors ${isCurrent ? "bg-sand-200 text-ink-500" : "bg-ink-900 text-white hover:bg-ink-700"}`}>
        Oggi
      </button>
    </div>
  );
}

function DayTile({ iso, stats, active, onClick }) {
  const isToday = iso === romeToday();
  return (
    <button type="button" onClick={onClick} data-testid={`week-day-${iso}`} aria-pressed={active}
            className={`min-h-[76px] rounded-2xl border px-1 py-2 flex flex-col items-center justify-center gap-0.5 transition-colors ${
              active ? "bg-ink-900 border-ink-900 text-white" : "bg-white border-sand-200 hover:border-bronze-300"}`}>
      <span className={`text-[11px] uppercase tracking-[0.12em] ${active ? "text-white/70" : "text-ink-500"}`}>{fmt(iso, { weekday: "short" })}</span>
      <span className={`font-data text-lg font-semibold leading-none ${isToday && !active ? "text-bronze-700" : ""}`}>{fmt(iso, { day: "numeric" })}</span>
      <span className={`font-data text-xs mt-1 ${stats.count ? "" : "opacity-50"}`} data-testid={`week-day-count-${iso}`}>
        {stats.count} <span className="hidden md:inline">pren.</span>
      </span>
      {stats.count > 0 && <span className={`font-data text-[11px] ${active ? "text-white/70" : "text-ink-500"}`} data-testid={`week-day-pax-${iso}`}>{stats.pax} cop.</span>}
    </button>
  );
}

export default function AgendaWeek({ date, rows, dayFilter, onDayFilter, onDateChange, perms, on }) {
  const start = weekStart(date);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => shiftDay(start, i)), [start]);
  const byDay = useMemo(() => {
    const m = new Map(days.map((d) => [d, []]));
    rows.forEach((r) => { if (m.has(r.date)) { m.get(r.date).push(r); } });
    m.forEach((list) => list.sort((a, b) => (a.time || "").localeCompare(b.time || "")));
    return m;
  }, [rows, days]);
  const total = dayStats(rows.filter((r) => byDay.has(r.date)));
  const shown = days.filter((d) => (dayFilter ? d === dayFilter : byDay.get(d).length > 0));
  let idx = 0;
  return (
    <div className="space-y-4" data-testid="agenda-week">
      <WeekNav start={start} onChange={(d) => { onDayFilter(null); onDateChange(d); }} />
      <div className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="week-days">
        {days.map((d) => (
          <DayTile key={d} iso={d} stats={dayStats(byDay.get(d))} active={dayFilter === d}
                   onClick={() => onDayFilter(dayFilter === d ? null : d)} />
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-ink-600" data-testid="week-total">
          <span className="font-data font-semibold text-ink-900">{total.count}</span> {total.count === 1 ? "prenotazione" : "prenotazioni"} ·{" "}
          <span className="font-data font-semibold text-ink-900">{total.pax}</span> coperti nella settimana
        </span>
        {dayFilter && (
          <button type="button" onClick={() => onDayFilter(null)} data-testid="week-show-all"
                  className="h-11 px-4 rounded-full border border-sand-300 bg-white text-sm font-medium hover:border-bronze-400">
            Tutta la settimana
          </button>
        )}
      </div>
      {shown.length === 0 || shown.every((d) => byDay.get(d).length === 0) ? (
        <div className="py-12 text-center rounded-2xl border border-dashed border-sand-300 bg-white/60 text-sm text-ink-500" data-testid="week-empty">
          {dayFilter ? "Nessuna prenotazione in questo giorno." : "Nessuna prenotazione in questa settimana."}
        </div>
      ) : shown.map((d) => {
        const list = byDay.get(d);
        const s = dayStats(list);
        return (
          <section key={d} data-testid={`week-group-${d}`}>
            <h3 className="font-sans text-sm font-semibold text-ink-700 mb-2 flex items-baseline gap-2">
              <span className="capitalize">{fmt(d, { weekday: "long", day: "numeric", month: "long" })}</span>
              <span className="font-data text-xs font-normal text-ink-500">{s.count} pren. · {s.pax} coperti</span>
            </h3>
            <ul className="space-y-2">
              {list.map((r) => <AgendaRow key={r.id} r={r} perms={perms} on={on} index={idx++} />)}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
